// OWNED BY the documents implementation agent (see ../DocumentsTab.tsx ownership note).
//
// The drag & click upload area of mock s19 (ct-drop) + inline per-file upload
// progress rows. Upload semantics are PORTED from MediaUpload.tsx (client →
// Firebase Storage → download URL) via documentsData.uploadContactDocument,
// with multi-file support, a flat 20MB cap and a PDF/image/Word accept list
// per the mock. Errors surface as Hebrew snackbars (notistack), never modals.

import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { Box, Stack, Typography, LinearProgress } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { CloudUpload } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import {
  MAX_UPLOAD_SIZE_BYTES,
  ACCEPTED_FILE_TYPES,
  uploadContactDocument
} from "./documentsData";

type UploadingItem = {
  key: string;
  name: string;
  progress: number; // 0..1
};

type Props = {
  contactId: string;
};

export function UploadDropzone({ contactId }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [uploads, setUploads] = useState<UploadingItem[]>([]);
  const keyCounterRef = useRef(0);

  const removeUpload = (key: string): void =>
    setUploads((current) => current.filter((item) => item.key !== key));

  const setProgress = (key: string, progress: number): void =>
    setUploads((current) =>
      current.map((item) => (item.key === key ? { ...item, progress } : item))
    );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (!contactId) return;
      acceptedFiles.forEach((file) => {
        keyCounterRef.current += 1;
        const key = `${Date.now()}_${keyCounterRef.current}`;
        setUploads((current) => [...current, { key, name: file.name, progress: 0 }]);

        void uploadContactDocument(contactId, file, (fraction) => setProgress(key, fraction))
          .then(() => {
            removeUpload(key);
          })
          .catch((error) => {
            console.error("Documents tab: upload failed", error);
            removeUpload(key);
            enqueueSnackbar(`העלאת ״${file.name}״ נכשלה`, {
              variant: "error",
              autoHideDuration: 4000
            });
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactId]
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      rejections.forEach(({ file, errors }) => {
        const tooLarge = errors.some((error) => error.code === "file-too-large");
        const message = tooLarge
          ? `״${file.name}״ גדול מ־20MB`
          : `״${file.name}״ — אפשר להעלות רק PDF, תמונה או Word`;
        enqueueSnackbar(message, { variant: "error", autoHideDuration: 4000 });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple: true,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    onDropAccepted,
    onDropRejected
  });

  return (
    <Box>
      {uploads.length > 0 && (
        <Stack gap={1} sx={{ mb: 1.5 }}>
          {uploads.map((item) => (
            <Box key={item.key} sx={{ px: 1 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Typography variant="caption" noWrap dir="auto" sx={{ minWidth: 0 }}>
                  מעלה את ״{item.name}״…
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                  {Math.round(item.progress * 100)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.round(item.progress * 100))}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          ))}
        </Stack>
      )}

      <Box
        {...getRootProps()}
        sx={{
          px: 2,
          py: 3.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0.75,
          borderRadius: 1.5,
          cursor: "pointer",
          textAlign: "center",
          color: "text.secondary",
          outline: "none",
          border: (theme) => `1px dashed ${theme.palette.divider}`,
          transition: (theme) => theme.transitions.create(["border-color", "background-color"]),
          "&:hover": {
            bgcolor: "action.hover"
          },
          ...(isDragActive && {
            borderColor: "primary.main",
            bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.08)
          }),
          ...(isDragReject && {
            borderColor: "error.main",
            bgcolor: (theme: any) => alpha(theme.palette.error.main, 0.08)
          })
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ color: "text.disabled" }} />
        <Typography variant="body2">
          גררו קובץ לכאן או <Box component="b" sx={{ color: "text.primary" }}>לחצו להעלאה</Box>
          {" · "}PDF, תמונה או Word · עד 20MB
        </Typography>
      </Box>
    </Box>
  );
}
